/**
 * WIL-201 — Multi-collection filter logic.
 *
 * Reimplements the legacy Porto filter behaviour clean (vanilla JS, no jQuery).
 * URL contract preserved 1:1 — see docs/URL_CONTRACT.md:
 *   - collections=h1,h2            (comma-separated collection handles)
 *   - collection_set=h1~h2         (same handles; pageurl is cut at a comma)
 *   - tags=Part Classification:X   (tilde- or comma-separated, OR within part type)
 *   - price_min / price_max        (cents, always set as a pair)
 *   - vehicle path segments        (/+make_X+model_Y — preserved, never rebuilt here)
 *   - single-collection shortcut   (exactly one collection, no other filters → /collections/{handle})
 *   - anti-redirect flags          (sessionStorage.justFiltered / categoryFilterApplied)
 *
 * Native Shopify `filter.p.tag` / Storefront filtering is not used (WIL-461):
 * those URLs would break the SEO-critical tags=/price_min contract.
 */
(function () {
  var CLASSIFICATION_PREFIX = 'Part Classification:';
  var VEHICLE_PREFIXES = ['make_', 'model_', 'year_', 'series_'];

  /** Parse the current URL into filter state. */
  function readState() {
    var params = new URLSearchParams(window.location.search);

    var collections = [];
    // collection_set keeps every ticked location. collections= is the contract
    // form, and pageurl drops whatever follows its comma (WIL-692).
    var collectionsParam = params.get('collection_set') || params.get('collections');
    if (collectionsParam) {
      collections = collectionsParam.split(/[~,]/).map(function (h) {
        return h.trim();
      }).filter(Boolean);
    }

    var classifications = [];
    var tagsParam = params.get('tags');
    if (tagsParam) {
      tagsParam.split(/[~,]/).forEach(function (raw) {
        // URLSearchParams has already percent-decoded and turned '+' into
        // spaces — decoding again threw on literal '%' and corrupted '+'.
        var tag = raw.trim();
        if (tag.indexOf(CLASSIFICATION_PREFIX) === 0) {
          classifications.push(tag.slice(CLASSIFICATION_PREFIX.length));
        }
      });
    }

    var priceMin = params.get('price_min');
    var priceMax = params.get('price_max');

    return {
      collections: collections,
      classifications: classifications,
      // Contract stores cents; UI works in whole dollars (non-whole-dollar
      // cents can only arrive via a hand-crafted URL and round to the dollar).
      priceMin: priceMin != null ? Math.round(Number(priceMin) / 100) : null,
      priceMax: priceMax != null ? Math.round(Number(priceMax) / 100) : null,
      vehicleSegments: readVehicleSegments(),
      baseHandle: readBaseHandle(),
    };
  }

  /** Collection handle from /collections/{handle}/… */
  function readBaseHandle() {
    var parts = window.location.pathname.split('/');
    return parts[1] === 'collections' && parts[2] ? parts[2] : null;
  }

  /**
   * Parent collection from the PLP breadcrumb back link (← Grilles).
   * Used when removing the sole Part location chip on a single-collection page.
   */
  function readBreadcrumbBackUrl() {
    var back = document.querySelector('[data-collection-page] .collection-page__breadcrumb-back');
    if (!(back instanceof HTMLAnchorElement)) return null;
    var dest = document.createElement('a');
    dest.href = back.href;
    var here = window.location.pathname.replace(/\/$/, '') + window.location.search;
    var next = dest.pathname.replace(/\/$/, '') + dest.search;
    if (!dest.pathname || next === here) return null;
    return dest.pathname + dest.search;
  }

  /** Active Shopify storefront vendor filters from the query string. */
  function readVendorFilters() {
    var params = new URLSearchParams(window.location.search);
    var vendors = [];
    params.forEach(function (value, key) {
      if (key === 'filter.p.vendor' || key === 'filter.v.vendor') {
        vendors.push(value);
      }
    });
    return vendors;
  }

  /** Single-collection PLP uses native ?filter.p.vendor= params (not WIL-201 search URLs). */
  function usesStorefrontVendorFilters() {
    var baseHandle = readBaseHandle();
    if (!baseHandle || baseHandle === 'search' || baseHandle === 'all') return false;
    var state = readState();
    return (
      state.collections.length === 0 &&
      state.classifications.length === 0 &&
      state.priceMin == null &&
      state.priceMax == null
    );
  }

  /** @param {HTMLElement} root */
  function buildVendorFilterUrl(root) {
    var url = new URL(window.location.href);
    var keys = [];
    url.searchParams.forEach(function (_, key) {
      if (key === 'filter.p.vendor' || key === 'filter.v.vendor') {
        keys.push(key);
      }
    });
    keys.forEach(function (key) {
      url.searchParams.delete(key);
    });
    root.querySelectorAll('[data-filter-vendor]:checked').forEach(function (input) {
      url.searchParams.append(input.name, input.value);
    });
    return url.pathname + url.search;
  }

  /**
   * Vehicle segments live in the path after the handle (e.g. +make_Toyota+model_Corolla).
   * They are preserved verbatim — the vehicle selector (WIL-207) owns writing them.
   */
  function readVehicleSegments() {
    var parts = window.location.pathname.split('/');
    if (parts[1] !== 'collections' || parts.length < 4) return [];
    var segment = parts[3] || '';
    if (!segment) return [];

    // Split the raw path on '+' first, then decode each chunk — decoding
    // before splitting corrupts values containing an encoded '+' (%2B).
    return segment
      .split('+')
      .map(function (chunk) {
        try {
          return decodeURIComponent(chunk);
        } catch {
          return chunk;
        }
      })
      .filter(function (chunk) {
        return VEHICLE_PREFIXES.some(function (prefix) {
          return chunk.indexOf(prefix) === 0;
        });
      });
  }

  /**
   * Current query params minus the contract params this module owns (and
   * `page`, which must reset on filter change) — preserves sort_by, vendor
   * filters, q, etc. across filter navigations.
   */
  function preservedParams() {
    var params = new URLSearchParams(window.location.search);
    ['collections', 'collection_set', 'tags', 'price_min', 'price_max', 'page'].forEach(function (key) {
      params.delete(key);
    });
    return params;
  }

  /**
   * Build the destination URL from filter state — mirrors legacy applyAllFilters
   * (js-filter-menu-accordion.liquid ~L860–930).
   */
  function buildUrl(state) {
    var hasClassifications = state.classifications.length > 0;
    var hasPrice = state.priceMin != null || state.priceMax != null;
    var hasVehicle = state.vehicleSegments.length > 0;

    // Single-collection shortcut: exactly one collection, no classification or
    // price filters. A vehicle rides along as the native path-filter (contract
    // §2) — Shopify then filters the whole collection by tag server-side. The
    // search multi grid can only scan 50 products per collection in Liquid, so
    // vehicle matches deep in a large collection were missed entirely.
    if (state.collections.length === 1 && !hasClassifications && !hasPrice) {
      var shortcutUrl =
        '/collections/' + state.collections[0] + vehiclePathSuffix(state.vehicleSegments);
      var shortcutQuery = preservedParams().toString();
      return shortcutUrl + (shortcutQuery ? '?' + shortcutQuery : '');
    }

    // Contract §2: every vehicle segment carries a leading '+', including
    // the first, and segment values are percent-encoded.
    var url = '/collections/search' + vehiclePathSuffix(state.vehicleSegments);

    var params = preservedParams();
    // Tilde lists go first. collections= still uses a comma for the URL
    // contract, and that comma is what makes pageurl drop the rest.
    if (hasClassifications) {
      params.set(
        'tags',
        state.classifications
          .map(function (value) {
            return CLASSIFICATION_PREFIX + value;
          })
          .join('~')
      );
    }
    if (hasPrice) {
      // Contract: cents. The UI always sets both; a single-param inbound URL
      // round-trips as-is (fabricating price_max=0 filtered out everything).
      if (state.priceMin != null) params.set('price_min', String(state.priceMin * 100));
      if (state.priceMax != null) params.set('price_max', String(state.priceMax * 100));
    }
    if (state.collections.length > 0) {
      params.set('collection_set', state.collections.join('~'));
      params.set('collections', state.collections.join(','));
    }

    var query = params.toString();
    return query ? url + '?' + query : url;
  }

  /** Vehicle path suffix (contract §2) — '' when no vehicle is active. */
  function vehiclePathSuffix(segments) {
    if (!segments.length) return '';
    return (
      '/+' +
      segments
        .map(function (segment) {
          return encodeURIComponent(segment);
        })
        .join('+')
    );
  }

  /** Anti-redirect flags — must be set before navigation (contract §7). */
  function setAntiRedirectFlags() {
    sessionStorage.setItem('justFiltered', 'true');
    sessionStorage.setItem('categoryFilterApplied', Date.now().toString());
  }

  /**
   * @param {string} url
   * @param {boolean} [flagFilterApply] Legacy sets the anti-redirect flags only
   *   when a category filter is applied — not on clear-all or vendor-only
   *   navigations. The WIL-207 vehicle selector consumes (and must clear)
   *   these exact keys.
   */
  function navigate(url, flagFilterApply) {
    var dest = document.createElement('a');
    dest.href = url;
    var here = window.location.pathname.replace(/\/$/, '') + window.location.search;
    var next = dest.pathname.replace(/\/$/, '') + dest.search;
    if (next === here) return;

    if (flagFilterApply !== false) setAntiRedirectFlags();
    window.location.href = url;
  }

  /* ---------------------------------------------------------------- UI --- */

  /** @param {HTMLElement} root */
  function collectUiState(root) {
    var state = readState();
    var urlCollections = state.collections;

    state.collections = Array.prototype.map.call(
      root.querySelectorAll('[data-filter-collection]:checked'),
      function (input) {
        return input.value;
      }
    );

    // Keep URL collection handles that have no checkbox in this sidebar (the
    // list is menu-derived) — otherwise they'd silently drop on the next
    // filter change. Handles whose checkbox exists but is unchecked were
    // deliberately removed by the user.
    var knownHandles = Array.prototype.map.call(
      root.querySelectorAll('[data-filter-collection]'),
      function (input) {
        return input.value;
      }
    );
    urlCollections.forEach(function (handle) {
      if (knownHandles.indexOf(handle) === -1 && state.collections.indexOf(handle) === -1) {
        state.collections.push(handle);
      }
    });

    state.classifications = Array.prototype.map.call(
      root.querySelectorAll('[data-filter-classification]:checked'),
      function (input) {
        return input.value;
      }
    );

    var minInput = root.querySelector('[data-filter-price-min]');
    var maxInput = root.querySelector('[data-filter-price-max]');
    var min = minInput && minInput.value !== '' ? Number(minInput.value) : null;
    var max = maxInput && maxInput.value !== '' ? Number(maxInput.value) : null;
    var defaultMin = minInput ? Number(minInput.min || 0) : 0;
    var defaultMax = maxInput ? Number(maxInput.max || 0) : 0;

    // Legacy omits price params when the slider sits at its defaults.
    if (
      (min === defaultMin || min == null) &&
      (max === defaultMax || max == null)
    ) {
      state.priceMin = null;
      state.priceMax = null;
    } else {
      state.priceMin = min != null ? min : defaultMin;
      state.priceMax = max != null ? max : defaultMax;
    }

    // On a single-collection PLP, scope classification/price filters to the
    // current handle when no Part location boxes are checked (legacy parity).
    var baseHandle = readBaseHandle();
    var hasNonCollectionFilters =
      state.classifications.length > 0 ||
      state.priceMin != null ||
      state.priceMax != null;

    if (
      state.collections.length === 0 &&
      hasNonCollectionFilters &&
      baseHandle &&
      baseHandle !== 'search' &&
      baseHandle !== 'all'
    ) {
      state.collections = [baseHandle];
    }

    return state;
  }

  /**
   * Vendor filters only work through native ?filter.p.vendor= params, which the
   * multi-collection contract URLs don't support — hide the Brand group when it
   * would be inert (search/all hubs or when contract filters are active).
   * @param {HTMLElement} root
   */
  function syncBrandGroupVisibility(root) {
    var brandOptions = root.querySelector('[data-filter-brand-options]');
    if (!brandOptions) return;
    var group = brandOptions.closest('details');
    if (group) group.hidden = !usesStorefrontVendorFilters();
  }

  /** Restore checkbox/price state from the current URL. @param {HTMLElement} root */
  function hydrate(root) {
    var state = readState();

    syncBrandGroupVisibility(root);

    root.querySelectorAll('[data-filter-collection]').forEach(function (input) {
      input.checked = state.collections.indexOf(input.value) !== -1;
    });

    // Single-collection PLP has no collections= param, but the current handle
    // is still the active Part location — show it checked so a click is not a no-op.
    var baseHandle = readBaseHandle();
    if (
      state.collections.length === 0 &&
      baseHandle &&
      baseHandle !== 'search' &&
      baseHandle !== 'all'
    ) {
      root.querySelectorAll('[data-filter-collection]').forEach(function (input) {
        if (input.value === baseHandle) input.checked = true;
      });
    }

    root.querySelectorAll('[data-filter-classification]').forEach(function (input) {
      input.checked = state.classifications.indexOf(input.value) !== -1;
    });

    var vendors = readVendorFilters();
    root.querySelectorAll('[data-filter-vendor]').forEach(function (input) {
      if (vendors.length > 0) {
        input.checked = vendors.indexOf(input.value) !== -1;
      }
    });

    var minInput = root.querySelector('[data-filter-price-min]');
    var maxInput = root.querySelector('[data-filter-price-max]');
    // Widen the page-local slider bounds to the URL values before assigning —
    // native range inputs clamp silently, and a clamped-to-default value is
    // then treated as "no filter" and dropped from the next URL.
    if (minInput && state.priceMin != null) {
      if (Number(minInput.min || 0) > state.priceMin) minInput.min = String(state.priceMin);
      minInput.value = String(state.priceMin);
    }
    if (maxInput && state.priceMax != null) {
      if (Number(maxInput.max || 0) < state.priceMax) maxInput.max = String(state.priceMax);
      maxInput.value = String(state.priceMax);
    }
    syncPriceOutputs(root);

    openActiveLocationGroups(root);
    updateChips(root, state);
    updateClearState(root);
  }

  /** Keep ancestor Part location folders open when a leaf is checked. */
  function openActiveLocationGroups(root) {
    root.querySelectorAll('.collection-filter__location-group').forEach(function (group) {
      if (group.querySelector('[data-filter-collection]:checked')) group.open = true;
    });
  }

  /** @param {HTMLElement} root */
  function currencySymbol(root) {
    return (root && root.dataset && root.dataset.currencySymbol) || '$';
  }

  /** @param {HTMLElement} root */
  function syncPriceOutputs(root) {
    var minInput = root.querySelector('[data-filter-price-min]');
    var maxInput = root.querySelector('[data-filter-price-max]');
    var minLabel = root.querySelector('[data-filter-price-min-label]');
    var maxLabel = root.querySelector('[data-filter-price-max-label]');
    var fill = root.querySelector('[data-filter-price-fill]');
    if (!minInput || !maxInput) return;

    var floor = Number(minInput.min || 0);
    var min = Number(minInput.value || floor);
    var max = Number(maxInput.value || maxInput.max || 0);
    var ceiling = Number(maxInput.max || 0);
    var range = ceiling - floor;

    if (min > max) {
      minInput.value = String(max);
      min = max;
    }

    if (min < floor) {
      minInput.value = String(floor);
      min = floor;
    }

    if (minLabel) minLabel.textContent = currencySymbol(root) + min;
    if (maxLabel) maxLabel.textContent = currencySymbol(root) + max;

    if (fill && range > 0) {
      var left = ((min - floor) / range) * 100;
      var width = ((max - min) / range) * 100;
      fill.hidden = false;
      fill.style.left = left + '%';
      fill.style.width = width + '%';
    }
  }

  /**
   * Collection handles shown as chips — includes implicit single-PLP selection
   * when the URL has no collections= param but the sidebar checkbox is checked.
   * @param {HTMLElement} root
   * @param {{ collections: string[] }} state
   */
  function collectionChipHandles(root, state) {
    if (state.collections.length > 0) {
      return state.collections.slice();
    }
    return Array.prototype.map.call(
      root.querySelectorAll('[data-filter-collection]:checked'),
      function (input) {
        return input.value;
      }
    );
  }

  /** Applied-filter chips above the grid. @param {HTMLElement} root */
  function updateChips(root, state) {
    var chipHost = document.querySelector('[data-filter-chips]');
    if (!chipHost) return;
    chipHost.textContent = '';

    var chips = [];
    collectionChipHandles(root, state).forEach(function (handle) {
      var input = root.querySelector('[data-filter-collection][value="' + handle + '"]');
      var label = input ? input.getAttribute('data-label') || handle : handle;
      chips.push({ label: label, kind: 'collection', value: handle });
    });
    state.classifications.forEach(function (value) {
      var label = value;
      root.querySelectorAll('[data-filter-classification]').forEach(function (input) {
        if (input.value === value) {
          label = input.getAttribute('data-label') || value;
        }
      });
      chips.push({ label: label, kind: 'classification', value: value });
    });
    if (state.priceMin != null || state.priceMax != null) {
      var symbol = currencySymbol(root);
      chips.push({
        label: symbol + (state.priceMin || 0) + ' – ' + symbol + (state.priceMax || 0),
        kind: 'price',
        value: 'price',
      });
    }
    readVendorFilters().forEach(function (vendor) {
      // Value comparison instead of an attribute selector — vendor names come
      // from the URL and may contain quotes.
      var label = vendor;
      root.querySelectorAll('[data-filter-vendor]').forEach(function (input) {
        if (input.value === vendor) {
          label = input.getAttribute('data-label') || vendor;
        }
      });
      chips.push({ label: label, kind: 'vendor', value: vendor });
    });

    chips.forEach(function (chip) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'collection-filter__chip';
      button.setAttribute('data-chip-kind', chip.kind);
      button.setAttribute('data-chip-value', chip.value);
      // textContent, not innerHTML — chip labels can echo URL params (tags=, filter.p.vendor=).
      var labelSpan = document.createElement('span');
      labelSpan.textContent = chip.label;
      var closeSpan = document.createElement('span');
      closeSpan.setAttribute('aria-hidden', 'true');
      closeSpan.textContent = '\u00d7';
      button.append(labelSpan, closeSpan);
      button.addEventListener('click', function () {
        removeChip(root, chip);
      });
      chipHost.appendChild(button);
    });

    chipHost.hidden = chips.length === 0;
  }

  function removeChip(root, chip) {
    var state = collectUiState(root);
    if (chip.kind === 'collection') {
      var pageHandle = readBaseHandle();
      var nextCollections = state.collections.filter(function (h) {
        return h !== chip.value;
      });
      var hasNonCollectionFilters =
        state.classifications.length > 0 ||
        state.priceMin != null ||
        state.priceMax != null;
      if (
        nextCollections.length === 0 &&
        hasNonCollectionFilters &&
        pageHandle &&
        pageHandle !== 'search' &&
        pageHandle !== 'all'
      ) {
        state.collections = [pageHandle];
      } else if (
        nextCollections.length === 0 &&
        pageHandle &&
        pageHandle !== 'search' &&
        pageHandle !== 'all' &&
        chip.value === pageHandle
      ) {
        // Same vehicle rule as the checkbox: keep the car, drop the location.
        if (state.vehicleSegments.length > 0) {
          state.collections = [];
          navigate(buildUrl(state));
          return;
        }
        var parentUrl = readBreadcrumbBackUrl();
        if (parentUrl) {
          navigate(parentUrl, false);
        }
        return;
      } else {
        state.collections = nextCollections;
      }
    } else if (chip.kind === 'classification') {
      state.classifications = state.classifications.filter(function (v) {
        return v !== chip.value;
      });
    } else if (chip.kind === 'price') {
      state.priceMin = null;
      state.priceMax = null;
    } else if (chip.kind === 'vendor') {
      root.querySelectorAll('[data-filter-vendor]').forEach(function (input) {
        if (input.value === chip.value) input.checked = false;
      });
      if (usesStorefrontVendorFilters()) {
        navigate(buildVendorFilterUrl(root), false);
        return;
      }
    }
    navigate(buildUrl(state));
  }

  /** @param {HTMLElement} root */
  function updateClearState(root) {
    var clear = root.querySelector('[data-filter-clear]');
    if (!clear) return;
    var uiState = collectUiState(root);
    var anyActive =
      uiState.classifications.length > 0 ||
      uiState.priceMin != null ||
      uiState.priceMax != null ||
      root.querySelectorAll('[data-filter-vendor]:checked').length > 0 ||
      (uiState.collections.length > 0 &&
        !(
          uiState.collections.length === 1 &&
          uiState.collections[0] === readBaseHandle()
        ));
    clear.disabled = !anyActive;
  }

  /** @param {HTMLElement} root */
  function init(root) {
    hydrate(root);

    root.addEventListener('change', function (event) {
      var target = event.target;
      if (!(target instanceof HTMLInputElement)) return;

      if (target.matches('[data-filter-price-min], [data-filter-price-max]')) {
        syncPriceOutputs(root);
        navigate(buildUrl(collectUiState(root)));
        return;
      }

      if (
        target.matches('[data-filter-classification]') ||
        target.matches('[data-filter-collection]')
      ) {
        // Multi-select is the contract (§3: collections=h1,h2) and legacy
        // behaviour — forcing single-select made multi-collection URLs
        // unreachable from the UI (WIL-326 finding).
        var nextState = collectUiState(root);
        var pageHandle = readBaseHandle();
        if (
          target.matches('[data-filter-collection]') &&
          !target.checked &&
          nextState.collections.length === 0 &&
          pageHandle &&
          target.value === pageHandle
        ) {
          // With a vehicle active, dropping the last location goes back to
          // all parts for that vehicle — the breadcrumb parent loses the car.
          if (nextState.vehicleSegments.length > 0) {
            navigate(buildUrl(nextState));
            return;
          }
          var parentUrl = readBreadcrumbBackUrl();
          if (parentUrl) {
            navigate(parentUrl, false);
            return;
          }
          target.checked = true;
          return;
        }
        navigate(buildUrl(nextState));
        return;
      }

      if (target.matches('[data-filter-vendor]') && usesStorefrontVendorFilters()) {
        navigate(buildVendorFilterUrl(root), false);
      }

      updateClearState(root);
    });

    root.addEventListener('input', function (event) {
      var target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.matches('[data-filter-price-min], [data-filter-price-max]')) {
        syncPriceOutputs(root);
      }
    });

    var clearButton = root.querySelector('[data-filter-clear]');
    if (clearButton) {
      clearButton.addEventListener('click', function () {
        // Clearing filters is not a filter apply — don't set the
        // anti-redirect flags (legacy parity, contract §7). The vehicle is
        // not a card filter — it has its own chip and clear — so keep it.
        var vehiclePath = vehiclePathSuffix(readVehicleSegments());
        var baseHandle = readBaseHandle();
        if (baseHandle && baseHandle !== 'search' && baseHandle !== 'all') {
          navigate('/collections/' + baseHandle + vehiclePath, false);
          return;
        }
        var state = readState();
        if (state.collections.length === 1) {
          navigate('/collections/' + state.collections[0] + vehiclePath, false);
          return;
        }
        navigate('/collections/search' + vehiclePath, false);
      });
    }
  }

  function boot() {
    document.querySelectorAll('[data-collection-filter-slot]').forEach(function (root) {
      if (!(root instanceof HTMLElement) || root.dataset.filtersInit) return;
      // Keyword search owns its own wiring (search-filters.js) so q is kept.
      if (root.hasAttribute('data-search-filter-slot')) return;
      root.dataset.filtersInit = 'true';
      init(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);

  // Exposed for the vehicle selector (WIL-207) and debugging.
  window.MAPFilters = { readState: readState, buildUrl: buildUrl };
})();
